import { Cancel, CheckCircleOutline, Construction, Delete, LockOpen, LockOutlined } from "@mui/icons-material"
import { Box, Link, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { API } from "../../../lib/api"
import useRevenueLinesFlatColumns from "../../../lib/TableColumns/Modules/RevenueLinesFlatColumns"
import { gridFilteredSortedRowIdsSelector, GridFooterContainer, GridPagination, gridRowsLookupSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid"
import DeleteModal from "../../../components/DeleteModal/DeleteModal"
import RecordModal from "../../../components/RecordModal/RecordModal"
import Feedback from "../../../components/Feedback/Feedback"
import CollapsibleGridCard from "../../../components/CollapsibleGridCard"

function TotalsFooter() {
    const apiRef = useGridApiContext();
    const filteredIds = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);
    const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
    const visibleRows = filteredIds.map((id) => rowsLookup[id]);

    const totalRevenue = visibleRows.reduce((s, r) => s + (Number(r?.weightedAmount) || 0), 0);
    const totalMargin  = visibleRows.reduce((s, r) => s + (Number(r?.marginAmount) || 0), 0);
    const totalNonWeightedRevenue = visibleRows.reduce((s, r) => s + (Number(r?.amount) || 0), 0);
    const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

    return (
        <GridFooterContainer
            sx={{
                px: 2,
                py: 1,
                gap: 2,
                alignItems: "center",
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#f9fafb",
                display: "flex",
            }}
        >
            <Box sx={{ display: "flex", gap: 4 }}>
                <Typography variant="subtitle2" color={"#d21919"}><strong>Total Revenue (Non Weighted):</strong> {fmt(totalNonWeightedRevenue)}</Typography>
                <Typography variant="subtitle2" color={"#1976d2"}><strong>Total Revenue:</strong> {fmt(totalRevenue)}</Typography>
                <Typography variant="subtitle2" color={"#9c27b0"}><strong>Total Margin:</strong> {fmt(totalMargin)}</Typography>
            </Box>

            <Box sx={{ flex: 1 }} /> {/* spacer pushes pagination to the right */}

            {/* 👇 bring back the default pagination controls */}
            <GridPagination />
        </GridFooterContainer>
    );
}

const SavedForecasts = () => {
    const [forecasts, setForecasts] = useState([])
    const [activeForecast, setActiveForecast] = useState("")
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedRows, setSelectedRows] = useState([]);
    const [feedback,setFeedback] = useState({})

    const revLineColumns = useRevenueLinesFlatColumns()

    const handleForecastSelection = (id) => {
        const found = forecasts.find(f => f._id === id);
        setActiveForecast(found || null);
    };

    const handleSelectionChange = (selection) => setSelectedRows(selection)

    const handleOpenModal = () => selectedRows.length ? setModalOpen(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseDelete = () => setDeleteModalOpen(false)
    const handleDeleteOpen = () => selectedRows.length ? setDeleteModalOpen(true) : setFeedback({open: true, message: "Select at least one record to delete!", type: "info"})
    const handleCloseModal = () => setModalOpen(false);

    const handleDelete = async () => {
        const response = await API.Forecasting.Delete(selectedRows)
        if (response.status === 200) {
            setForecasts(prevForecasts => prevForecasts.filter(f => !selectedRows.includes(f._id)) );
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'success' }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: 'error' }))
        }

        setActiveForecast("")
        handleCloseDelete()
        setSelectedRows([])
    }

    const handleSubmit = () => {
        setFeedback({open: true, message: "Update functionality not released...", type: "error"})
    }


    const columns = [
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            renderCell: ({ row: { _id, title } }) => (
                <Link component="button" type="button" onClick={() => {
                    handleForecastSelection(_id);
                }}>
                    {title}
                </Link>
            )
        },
        {
            field: "createdBy",
            headerName: "Created By",
            flex: 1,
            renderCell: ({ row: { createdBy } }) => createdBy ? `${createdBy.firstName} ${createdBy.lastName}` : 'Unassigned',
            valueGetter: (params) => `${params.firstName} ${params.lastName}`
        },
        {
            field: "createdAt",
            headerName: "Create Date",
            flex: 1,
            renderCell: ({ row: { createdAt } }) => {
                if (!createdAt) return "";
                const date = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
                return date.toISOString().split("T")[0]; // 'YYYY-MM-DD'
            },
        },
        {
            field: "validDataSet",
            headerName: "Data Set Valid",
            flex: 1,
            renderCell: ({ row: { validDataSet } }) => {
                return (
                    <Box
                        width={`60%`}
                        m="0 auto"
                        p="5px"
                        display="flex"
                        justifyContent="center"
                        backgroundColor={ validDataSet === true ? "#3da58a" : "#c53939" }
                        borderRadius="4px"
                        mt={1}
                    >
                        {validDataSet === true && <CheckCircleOutline />}
                        {validDataSet === false && <Cancel />}
                        <Typography color={"#e0e0e0"} sx={{ ml: "5px" }}>
                            {validDataSet ? "Pass" : "Fail"}
                        </Typography>
                    </Box>
                );
            },
        },

        {
            field: "isLocked",
            headerName: "Locked",
            flex: 1,
            renderCell: ({ row: { isLocked } }) => {
                return (
                    <Box
                        width={`60%`}
                        m="0 auto"
                        p="5px"
                        display="flex"
                        justifyContent="center"
                        backgroundColor={ isLocked === true ? "#c53939" : "#3da58a" }
                        borderRadius="4px"
                        mt={1}
                    >
                        {isLocked === true && <LockOutlined />}
                        {isLocked === false && <LockOpen />}
                        <Typography color={"#e0e0e0"} sx={{ ml: "5px" }}>
                            {isLocked ? "Locked" : "Open"}
                        </Typography>
                    </Box>
                );
            },
        },
    ];

    const actionButtons = [
        { title: "Update Selected Saved Forecast(s)", onClick: handleOpenModal, icon: Construction, color: "primary" },
        { title: "Delete Selected Saved Forecast(s)", onClick: handleDeleteOpen, icon: Delete, color: "error" },
    ];

    const updateFields = [
        { name: "isLocked", label: "Locked", type: "autocomplete", options: ["true", "false"]},
    ]

    useEffect(() => {
        async function fetchData() {
            const response = await API.Forecasting.FetchAll()
            setForecasts(response)
        }
        fetchData()
    },[])

    return (
        <>
            <DeleteModal open={deleteModalOpen} handleClose={handleCloseDelete} title={`Are you sure you want to delete (${selectedRows?.length}) Saved Forecast(s)?`} onDelete={handleDelete} />
            <RecordModal open={modalOpen} handleClose={handleCloseModal} fields={updateFields} title={"Update Saved Forecast(s)"} onSubmit={handleSubmit} />
            <Feedback props={feedback} setFeedback={setFeedback} />

            <CollapsibleGridCard
                title="Forecasting"
                linkLabel="Saved Forecasts"
                linkHref="#"
                rows={forecasts}
                columns={columns}
                getRowId={(row) => row._id}
                checkboxSelection
                onRowSelectionModelChange={handleSelectionChange}
                actionButtons={actionButtons}
                initialPageSize={25}
                pageSizeOptions={[25, 50, 100]}
            />


            <CollapsibleGridCard
                title="Saved Forecast"
                linkLabel={activeForecast?.title || "No Forecast Selected"}
                linkHref="#"
                rows={activeForecast?.lines || []}
                columns={revLineColumns}
                getRowId={(row) => row._id}
                checkboxSelection
                footerComponent={TotalsFooter}
                initialPageSize={25}
                pageSizeOptions={[25, 50, 100, 200, 300, 400, 500]}
            />

        </>

    )
}

export default SavedForecasts
